import { Box, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import SingleSelect from "../../../../../common/SingleSelect";
import BenefitsContainer from "../../../../form/components/benefits";
import { WebsiteUrlField } from "../../../../form/components/internal-details";
import CommercialInfoContainer from "../../../../form/components/commercial-info";
import CoordinatesInput from "../../../../form/components/CoordinatesInput";
import CountryMultiSelect from "../../../../form/components/CountryMultiSelect";
import LocationPinMapLinkField from "../../../../form/components/LocationPinMapLinkField";
import { FormikTextField } from "../../../../form/components/narrative/EditableFields";
import CarbonCreditingProgrammeSelect from "../../../../form/components/programme-info/CarbonCreditingProgrammeSelect";
import ReadyForSaleCheckbox from "../../../../form/components/ReadyForSaleCheckbox";
import NotReadyForSaleReasonSelect from "../../../../form/components/NotReadyForSaleReasonSelect";
import SatelliteImageryContainer from "../../../../form/components/satellite-imagery";
import ScoresContainer from "../../../../form/components/scores";
import ShortDescriptionField from "../../../../form/components/ShortDescriptionField";
import SDGSelect from "../../../../form/components/SDGSelect";
import type { EditSolutionFormValues } from "../../../../edit/form/types";
import {
  assetTypeOptions,
  interventionTypeOptions,
  mappedMethodologyField,
} from "../../../../utils";
import { richTextMaxWords } from "../../../../formatting/defaults";

import type { EarthlySectionEditFieldsProps } from "../../types";

function PublishingSummaryInputs() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <ReadyForSaleCheckbox />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <NotReadyForSaleReasonSelect />
        </Box>
      </Box>
      <WebsiteUrlField />
    </Box>
  );
}

function ProjectIntroductionInputs() {
  return <ShortDescriptionField />;
}

function KeyInformationInputs() {
  const { values } = useFormikContext<EditSolutionFormValues>();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SingleSelect
        name="intervention-type"
        label="Intervention Type"
        options={interventionTypeOptions}
        fieldPath="solution-intervention-type"
        warnInsteadOfError={true}
        value={values["solution-intervention-type"]}
      />
      <CountryMultiSelect />
      <SingleSelect
        name="asset-type"
        label="Asset Type (Credit Type)"
        options={assetTypeOptions}
        fieldPath="programme-info.asset-type"
        warnInsteadOfError={true}
        value={values["programme-info"]["asset-type"]}
      />
      <SingleSelect
        options={mappedMethodologyField}
        name="assessment-methodology"
        label="Assessment Methodology"
        fieldPath="solution-assessment-methodology"
        warnInsteadOfError={true}
        value={values["solution-assessment-methodology"]}
      />
      <CarbonCreditingProgrammeSelect />
    </Box>
  );
}

function ProjectInformationInputs() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SDGSelect />
      <FormikTextField
        name="narrative.project-information"
        id="project-information"
        label="Project Information"
        helperText="Paragraphs, lists and links are supported."
        maxWords={richTextMaxWords["project-information"]}
      />
    </Box>
  );
}

function MapInformationInputs() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CoordinatesInput />
      <SatelliteImageryContainer />
      <LocationPinMapLinkField />
    </Box>
  );
}

function ProjectNarrativesInputs() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography>Area Through Time</Typography>
      <FormikTextField
        name="narrative.area-through-time"
        id="area-through-time"
        label="Area Through Time"
        helperText="Paragraphs, lists and links are supported."
        maxWords={richTextMaxWords["area-through-time"]}
      />
      <Typography>Good For People</Typography>
      <FormikTextField
        name="narrative.good-for-people"
        id="good-for-people"
        label="Good For People"
        helperText="Paragraphs, lists and links are supported."
        maxWords={richTextMaxWords["good-for-people"]}
      />
      <Typography>Good For Earth</Typography>
      <FormikTextField
        name="narrative.good-for-earth"
        id="good-for-earth"
        label="Good For Earth"
        helperText="Paragraphs, lists and links are supported."
        maxWords={richTextMaxWords["good-for-earth"]}
      />
    </Box>
  );
}

function EarthlySectionEditInputs({ section }: EarthlySectionEditFieldsProps) {
  switch (section) {
    case "publishing-summary":
      return <PublishingSummaryInputs />;
    case "project-introduction":
      return <ProjectIntroductionInputs />;
    case "key-information":
      return <KeyInformationInputs />;
    case "scores":
      return <ScoresContainer open />;
    case "commercial-information":
      return <CommercialInfoContainer open />;
    case "project-information":
      return <ProjectInformationInputs />;
    case "benefits":
      return <BenefitsContainer open />;
    case "map-information":
      return <MapInformationInputs />;
    case "project-narratives":
      return <ProjectNarrativesInputs />;
  }
}

export default EarthlySectionEditInputs;
